import React, { useMemo, useState } from 'react'
import { Table, Button, Badge, ButtonGroup, Alert, Input, FormGroup } from 'reactstrap'
import { useQuery, useMutation } from '@apollo/client'
import { GET_ADMIN_INBOXES } from './Queries'
import { ARCHIVE_INBOX } from './Mutations'
import CreateEditInboxModal from './CreateEditInboxModal'

export default function InboxManagement() {
    const [showModal, setShowModal] = useState(false)
    const [selectedInbox, setSelectedInbox] = useState(null)
    const [showArchived, setShowArchived] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [feedback, setFeedback] = useState(null)

    const { data, loading, error, refetch } = useQuery(GET_ADMIN_INBOXES, {
        variables: { },
        fetchPolicy: 'network-only'
    })

    const [archiveInbox] = useMutation(ARCHIVE_INBOX)

    const inboxes = data?.myAdminInboxes?.edges?.map(edge => edge.node) || []

    const filteredInboxes = useMemo(() => {
        if (!searchTerm.trim()) {
            return inboxes
        }
        const lower = searchTerm.trim().toLowerCase()
        return inboxes.filter(inbox => inbox.name?.toLowerCase().includes(lower))
    }, [inboxes, searchTerm])

    const closeModal = () => {
        setShowModal(false)
        setSelectedInbox(null)
    }

    const handleCreate = () => {
        setSelectedInbox(null)
        setShowModal(true)
    }

    const handleEdit = (inbox) => {
        setSelectedInbox(inbox)
        setShowModal(true)
    }

    const handleArchive = (inbox, archive) => {
        if (window.confirm(`Are you sure you want to ${archive ? 'archive' : 'unarchive'} "${inbox.name}"?`)) {
            archiveInbox({
                variables: {
                    id: inbox.id,
                    isArchived: archive
                }
            })
                .then(({ data: mutationData }) => {
                    const response = mutationData?.messagingArchiveInbox
                    if (response?.ok) {
                        refetch()
                        setFeedback({
                            type: 'success',
                            message: `Inbox "${inbox.name}" ${archive ? 'archived' : 'restored'} successfully.`
                        })
                    } else {
                        setFeedback({
                            type: 'danger',
                            message: response?.errors?.join(', ') || 'Unable to update inbox status.'
                        })
                    }
                })
                .catch((mutationError) => {
                    setFeedback({
                        type: 'danger',
                        message: mutationError.message || 'Unexpected error updating inbox.'
                    })
                })
        }
    }

    const handleModalSuccess = (message) => {
        refetch()
        if (message) {
            setFeedback({
                type: 'success',
                message
            })
        }
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        )
    }

    if (error) {
        return <Alert color="danger">Error loading inboxes: {error.message}</Alert>
    }

    const showEmptyState = filteredInboxes.length === 0

    return (
        <div className="comms-admin-section">
            <div className="comms-admin-section-header">
                <div>
                    <h3>Inboxes</h3>
                    <p className="text-muted">Manage your messaging inboxes</p>
                </div>
                <div>
                    <Button color="primary" onClick={handleCreate}>
                        <i className="fa fa-plus"></i> Create Inbox
                    </Button>
                </div>
            </div>

            {feedback && (
                <Alert color={feedback.type} toggle={() => setFeedback(null)}>
                    {feedback.message}
                </Alert>
            )}

            <div className="comms-admin-filters mb-3">
                <FormGroup className="mb-0 flex-grow-1">
                    <Input
                        type="search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search inboxes…"
                    />
                </FormGroup>
            </div>

            {showEmptyState ? (
                <Alert color="info">
                    {searchTerm
                        ? 'No inboxes match your search.'
                        : showArchived 
                            ? 'No archived inboxes found.' 
                            : 'No inboxes found. Create your first inbox to get started.'}
                </Alert>
            ) : (
                <Table responsive striped hover className="comms-admin-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Channels</th>
                            <th>Members</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredInboxes.map((inbox) => (
                            <tr key={inbox.id}>
                                <td>
                                    <strong>{inbox.name}</strong>
                                </td>
                                <td>{inbox.channelsCount || 0}</td>
                                <td>{inbox.membersCount || 0}</td>
                                <td>
                                    {inbox.isArchived ? (
                                        <Badge color="secondary">Archived</Badge>
                                    ) : (
                                        <Badge color="success">Active</Badge>
                                    )}
                                </td>
                                <td>
                                    <ButtonGroup size="sm">
                                        <Button
                                            color="info"
                                            outline
                                            onClick={() => handleEdit(inbox)}
                                            title="Edit"
                                        >
                                            <i className="fa fa-edit"></i>
                                        </Button>
                                        <Button
                                            color={inbox.isArchived ? "success" : "warning"}
                                            outline
                                            onClick={() => handleArchive(inbox, !inbox.isArchived)}
                                            title={inbox.isArchived ? "Unarchive" : "Archive"}
                                        >
                                            <i className={`fa fa-${inbox.isArchived ? 'undo' : 'archive'}`}></i>
                                        </Button>
                                    </ButtonGroup>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <CreateEditInboxModal
                isOpen={showModal}
                toggle={closeModal}
                inbox={selectedInbox}
                onSuccess={handleModalSuccess}
            />
        </div>
    )
}
